<template>
  <CContainer>
    <CRow class="justify-content-center">
      <CCol md="12" class="text-start">
        <CAlert
          v-if="reqResponse.message"
          class="mt-4"
          :color="reqResponse.type"
          :show="!!reqResponse.message"
        >
          {{ reqResponse.message }}
        </CAlert>
        <CForm class="mb-2">
          <CRow>
            <CCol sm="6">
              <DatePicker
                v-model="v$.form.from_date.$model"
                class="cs-date-picker"
                :masks="{ L: 'MM/DD/YYYY' }"
                placeholder="Start date"
                :min-date="today"
                :max-date="maxCalendarDate"
                @update:model-value="onStartDateChange"
              >
                <template #default="{ inputValue, togglePopover }">
                  <div
                    class="input-icon-field"
                    @click="toggleDate(togglePopover)"
                  >
                    <div class="form-group">
                      <input
                        class="form-control"
                        :class="{
                          'is-invalid':
                            v$.form.from_date.$error ||
                            reqResponse.errors['from_date']
                        }"
                        placeholder="Select start date"
                        readonly
                        :value="inputValue"
                      />
                      <div
                        v-if="
                          v$.form.$error && v$.form.from_date.required.$invalid
                        "
                        class="invalid-feedback"
                      >
                        This field is required
                      </div>
                      <div
                        v-if="reqResponse.errors['from_date']"
                        class="invalid-feedback"
                      >
                        {{ reqResponse.errors["from_date"][0] }}
                      </div>
                    </div>
                    <span class="field-icon">
                      <i class="fi flaticon-calendar me-1" />
                    </span>
                  </div>
                </template>
              </DatePicker>
            </CCol>
            <CCol sm="6">
              <DatePicker
                v-model="v$.form.to_date.$model"
                class="cs-date-picker"
                :masks="{ L: 'MM/DD/YYYY' }"
                placeholder="End date"
                :popover="{ visibility: 'click' }"
                :min-date="
                  form.from_date && !editablePassBlock ? form.from_date : today
                "
                :max-date="maxCalendarDate"
              >
                <template #default="{ inputValue, togglePopover }">
                  <div class="input-icon-field" @click="togglePopover">
                    <div class="form-group">
                      <input
                        class="form-control"
                        :class="{
                          'is-invalid':
                            v$.form.to_date.$error ||
                            reqResponse.errors['to_date']
                        }"
                        placeholder="Select end date"
                        readonly
                        :value="inputValue"
                      />
                      <div
                        v-if="v$.form.$error && !v$.form.to_date.required"
                        class="invalid-feedback"
                      >
                        This field is required
                      </div>
                      <div
                        v-if="reqResponse.errors['to_date']"
                        class="invalid-feedback"
                      >
                        {{ reqResponse.errors["to_date"][0] }}
                      </div>
                    </div>
                    <span class="field-icon">
                      <i class="fi flaticon-calendar me-1" />
                    </span>
                  </div>
                </template>
              </DatePicker>
            </CCol>
          </CRow>

          <CRow>
            <CCol sm="6" class="mb-3 mb-sm-0">
              <CustomTimePicker
                format="HH:mm"
                v-model="form.from_time"
                :disable-set-focus="true"
                :readonly="!isEditableFromDate"
                placeholder="Start Time"
                :class="{
                  'is-invalid': !!(
                    v$.form.from_time.$error || reqResponse.errors['from_time']
                  )
                }"
              >
                <template #footer>
                  <div
                    v-if="v$.form.$error && v$.form.from_time.required.$invalid"
                    class="invalid-feedback"
                  >
                    This field is required
                  </div>
                  <div
                    v-if="
                      v$.form.$error &&
                      !v$.form.from_time.required.$invalid &&
                      v$.form.from_time.minValue.$invalid
                    "
                    class="invalid-feedback"
                  >
                    Start time can not be in the past
                  </div>
                  <div
                    v-if="reqResponse.errors['from_time']"
                    class="invalid-feedback"
                  >
                    {{ reqResponse.errors["from_time"][0] }}
                  </div>
                </template>
              </CustomTimePicker>
            </CCol>
            <CCol sm="6" class="mb-3 mb-sm-0">
              <CustomTimePicker
                format="HH:mm"
                v-model="form.to_time"
                placeholder="End Time"
                :class="{
                  'is-invalid': !!(
                    v$.form.to_time.$error || reqResponse.errors['to_time']
                  )
                }"
              >
                <template #footer>
                  <div
                    v-if="v$.form.$error && v$.form.to_time.required.$invalid"
                    class="invalid-feedback"
                  >
                    This field is required
                  </div>
                  <div
                    v-if="
                      v$.form.$error &&
                      !v$.form.to_time.required.$invalid &&
                      v$.form.to_time.minValue.$invalid
                    "
                    class="invalid-feedback"
                  >
                    End time must be greater than start time
                  </div>
                  <div
                    v-if="reqResponse.errors['to_time']"
                    class="invalid-feedback"
                  >
                    {{ reqResponse.errors["to_time"][0] }}
                  </div>
                </template>
              </CustomTimePicker>
            </CCol>

            <CCol sm="6" class="mb-3">
              <p class="mt-3 mb-1">
                <b>Pass types, included in this blocking period</b>
              </p>
              <CCheckBox
                label="Student Created"
                v-model="form.students"
                name="Student Created"
                :inline="true"
                :custom="true"
                class="custom-checkbox"
              />
              <CCheckBox
                label="Teacher Pass (Proxy Pass)"
                v-model="form.proxy"
                name="Proxy Pass"
                :inline="true"
                :custom="true"
                class="custom-checkbox"
              />
              <CCheckBox
                label="Appointment Pass"
                v-model="form.appointments"
                name="Appointment Pass"
                :inline="true"
                :custom="true"
                class="custom-checkbox"
              />

              <CCheckBox
                label="Kiosk"
                v-model="form.kiosk"
                name="Kiosk"
                :inline="true"
                :custom="true"
                class="custom-checkbox"
              />
            </CCol>

            <CCol sm="6" class="mb-3 mt-3">
              <VSelect
                v-model="form.recurrence_type"
                class="cs-select"
                :options="repeatOptions"
                :clearable="false"
                label="label"
                :reduce="(item) => item.value"
                @update:model-value="resetRepaetOptions"
              />
              <div
                v-if="form.recurrence_type === 'WEEKLY'"
                sm="6"
                class="mb-3 mb-sm-0"
              >
                <div
                  class="weekdays-options-container pt-0 pt-sm-2 d-flex justify-content-between"
                  :class="{ 'is-invalid': v$.form.recurrence_days.$error }"
                >
                  <div v-for="(day, index) in weekDays" :key="index">
                    <div
                      class="weekday-box"
                      :class="{
                        active: form.recurrence_days.find(
                          (el) => el === day.value
                        )
                      }"
                      @click="setWeekDay(day.value)"
                    >
                      {{ day.label }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="
                    v$.form.$error && v$.form.recurrence_days.required.$invalid
                  "
                  class="invalid-feedback mb-3"
                >
                  This field is required
                </div>
              </div>
            </CCol>
          </CRow>

          <CRow>
            <CCol sm="12">
              <div class="form-group">
                <textarea
                  v-model="form.reason"
                  class="form-control"
                  :class="{
                    'is-invalid':
                      v$.form.reason.$error || reqResponse.errors['reason']
                  }"
                  placeholder="Reason for blocking (shown to students and teachers)"
                  rows="1"
                />
                <div
                  v-if="v$.form.$error && v$.form.reason.required.$invalid"
                  class="invalid-feedback"
                >
                  This field is required
                </div>
                <div
                  v-if="reqResponse.errors['reason']"
                  class="invalid-feedback"
                >
                  {{ reqResponse.errors["reason"][0] }}
                </div>
              </div>
            </CCol>
            <CCol sm="12">
              <div class="form-group">
                <textarea
                  v-model="form.message"
                  class="form-control"
                  :class="{
                    'is-invalid':
                      v$.form.message.$error || reqResponse.errors['message']
                  }"
                  placeholder="Message to students who are out on pass"
                  rows="1"
                />
                <div
                  v-if="v$.form.$error && v$.form.message.required.$invalid"
                  class="invalid-feedback"
                >
                  This field is required
                </div>
                <div
                  v-if="reqResponse.errors['message']"
                  class="invalid-feedback"
                >
                  {{ reqResponse.errors["message"][0] }}
                </div>
              </div>
            </CCol>
          </CRow>

          <div class="form-group form-actions mt-4 text-center">
            <div class="d-flex align-items-center justify-content-center">
              <div
                v-if="editablePassBlock && editablePassBlock.id"
                class="d-flex"
              >
                <div @click="submit(true)">
                  <CLoadingButton
                    :spinner="isLoading"
                    :disabled="isLoading"
                    :loading="isLoading"
                    class="me-2 px-5 py-3 btn bg-gradient-blue text-white"
                  >
                    Update
                  </CLoadingButton>
                </div>
                <div class="ms-2" @click="cancelEdit()">
                  <CButton class="me-2 btn-gradient-border">
                    <span class="btn px-4"
                      ><b class="text-graident-blue">Cancel</b></span
                    >
                  </CButton>
                </div>
              </div>
              <div v-else @click="submit()">
                <CLoadingButton
                  :spinner="isLoading"
                  :disabled="isLoading"
                  :loading="isLoading"
                  class="me-2 btn px-5 py-3 bg-gradient-blue text-white"
                >
                  Submit
                </CLoadingButton>
              </div>
              <router-link
                v-if="!isInView"
                class="text-graident-blue ms-3"
                to="/limits/pass-blocking"
              >
                <div class="d-flex align-items-center" @click="goToFullMenu()">
                  <img
                    width="18"
                    class="m-0 me-2"
                    src="@/assets/images/icons/gradient-link.png"
                  />
                  <span class="text-graident-blue">Go to full menu</span>
                </div>
              </router-link>
            </div>
          </div>
        </CForm>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import useVuelidate from "@vuelidate/core"
import { required, requiredIf } from "vuelidate/lib/validators"
import moment from "moment-timezone"
// import { DatePicker } from "v-calendar"

export default {
  name: "CreatePassBlockingForm",
  props: ["isInView", "editablePassBlock"],
  emits: ["createdPassBlock", "closeModal"],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: false,
      form: {
        from_date: moment().format("MMM DD YYYY HH:mm:ss"),
        to_date: moment().format("MMM DD YYYY HH:mm:ss"),
        from_time: moment().format("HH:mm"),
        to_time: moment().add(1, "hour").format("HH:mm"),
        students: true,
        proxy: true,
        appointments: true,
        kiosk: true,
        recurrence_type: null,
        recurrence_days: [],
        reason: "",
        message: "",
        isEditableFromDate: true
      },
      repeatOptions: [
        {
          label: "No Repetition",
          value: null
        },
        {
          label: "Daily",
          value: "DAILY"
        },
        {
          label: "Weekly",
          value: "WEEKLY"
        }
      ],
      weekDays: [
        {
          label: "Mo",
          value: "Monday"
        },
        {
          label: "Tu",
          value: "Tuesday"
        },
        {
          label: "We",
          value: "Wednesday"
        },
        {
          label: "Th",
          value: "Thursday"
        },
        {
          label: "Fr",
          value: "Friday"
        },
        {
          label: "Sa",
          value: "Saturday"
        },
        {
          label: "Su",
          value: "Sunday"
        }
      ],
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      }
    }
  },
  validations() {
    return {
      form: {
        from_date: {
          required
        },
        to_date: {
          required
        },
        from_time: {
          required,
          minValue: (value, model) => {
            if (this.editablePassBlock) {
              return true
            }
            return moment(
              moment(model.from_date).utc().format("MMM DD YYYY") +
                " " +
                model.from_time,
              "MMM DD YYYY HH:mm"
            ).isSameOrAfter(moment().startOf("minute"), "minute")
          }
        },
        to_time: {
          required,
          minValue: (value, model) => {
            return value > model.from_time
          }
        },
        students: {},
        proxy: {},
        appointments: {},
        kiosk: {},
        recurrence_type: {},
        recurrence_days: {
          required: requiredIf((model) => {
            return model.recurrence_type && model.recurrence_type === "weekly"
          })
        },
        reason: {
          required
        },
        message: {
          required
        }
      }
    }
  },
  computed: {
    isValid() {
      return !this.v$.form.$invalid
    },
    isEditableFromDate() {
      return this.editablePassBlock
        ? this.editablePassBlock.from_date &&
            this.$helpers.transformDate(
              this.editablePassBlock.from_date,
              "YYYY-MM-DD HH:mm:ss"
            ) >= this.$helpers.date().format("YYYY-MM-DD HH:mm:ss")
        : true
    },
    today() {
      return moment().format("MMM DD YYYY")
    },
    maxCalendarDate() {
      return this.$helpers.maxSchoolYearDate()
    }
  },
  watch: {
    editablePassBlock: function (passBlock) {
      this.resetForm()
      this.resetResponseMessages()
      this.setFormByPassRecord(passBlock)
    }
    // isEditableFromDate(value){
    //   this.form.isEditableFromDate = value;
    // }
  },
  unmounted() {
    this.cancelEdit()
  },
  methods: {
    resetRepaetOptions() {
      this.form.recurrence_days = []
      this.form.recurrence_week = null
      this.formatMontlyWeekDay(this.form.start_date)
    },
    formatMontlyWeekDay(date) {
      if (date) {
        const dayOfMonth = moment.utc(date).format("DD")
        const dayOfweek = moment.utc(date).format("dddd")
        const numberofweek = Math.floor((dayOfMonth - 1) / 7) + 1
        let prefix = ""
        switch (numberofweek) {
          case 1:
            prefix = "first"
            break
          case 2:
            prefix = "second"
            break
          case 3:
            prefix = "third"
            break
          case 4:
            prefix = "fourth"
            break
          default:
            prefix = "last"
        }
        this.form.recurrence_week =
          '{"' + numberofweek + '":"' + dayOfweek + '"}'
        this.repeatOptions[3].label =
          "Monthly on the " + prefix + " " + dayOfweek
      }
    },
    setWeekDay(day) {
      if (this.form.recurrence_days && this.form.recurrence_days.length) {
        if (this.form.recurrence_days.filter((el) => el === day).length) {
          this.form.recurrence_days = this.form.recurrence_days.filter(
            (el) => el !== day
          )
        } else {
          this.form.recurrence_days.push(day)
        }
      } else {
        this.form.recurrence_days.push(day)
      }
    },
    setFormByPassRecord(passBlockData) {
      const passBlock = passBlockData
        ? JSON.parse(JSON.stringify(passBlockData))
        : null
      if (passBlock) {
        this.form = {
          from_date:
            passBlock.recurrence_type == null
              ? moment(passBlock.from_date)._d
              : moment(passBlock.recurrence_start_at)._d,
          to_date:
            passBlock.recurrence_type == null
              ? moment(passBlock.to_date)._d
              : moment(passBlock.recurrence_end_at)._d,
          from_time: moment(passBlock.from_date).format("HH:mm"),
          to_time: moment(passBlock.to_date).format("HH:mm"),
          students: !!passBlock.students,
          proxy: !!passBlock.proxy,
          appointments: !!passBlock.appointments,
          kiosk: !!passBlock.kiosk,
          reason: passBlock.reason,
          message: passBlock.message
        }
        if (passBlock.recurrence_type) {
          this.form.recurrence_type = passBlock.recurrence_type
          this.form.recurrence_days = passBlock.recurrence_days
            ? passBlock.recurrence_days.days
            : null
        } else {
          this.form.recurrence_type = null
        }
      }
    },
    submit(isUpdate) {
      this.resetResponseMessages()
      if (this.isValid) {
        const inputsData = {
          from_date: this.$helpers.currTzDateTime(
            this.form.from_date,
            this.form.from_time
          ),
          to_date: this.$helpers.currTzDateTime(
            this.form.to_date,
            this.form.to_time
          ),
          students: this.form.students,
          proxy: this.form.proxy,
          appointments: this.form.appointments,
          kiosk: this.form.kiosk,
          recurrence_type: this.form.recurrence_type, //daily,weekly
          recurrence_days: this.form.recurrence_days
            ? JSON.stringify({ days: this.form.recurrence_days })
            : JSON.stringify({ days: [] }), //{"days": ["Monday","Friday"]}
          reason: this.form.reason,
          message: this.form.message
        }

        if (isUpdate && this.editablePassBlock) {
          //console.log(inputsData)
          this.updatePassBlock(inputsData)
        } else {
          this.createPassBlock(inputsData)
        }
      } else {
        this.v$.$touch()
      }
    },
    createPassBlock(data) {
      this.isLoading = true
      this.$store
        .dispatch("passBlocks/createPassBlock", data)
        .then((response) => {
          const data = response.data.data
          if (data) {
            setTimeout(() => {
              this.$emit("createdPassBlock")
            }, 500)
          }
          this.resetForm()
          this.setSuccessResponse("Successfully created.")
          this.isLoading = false
          setTimeout(() => {
            this.resetResponseMessages()
          }, 1000)
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
          this.isLoading = false
        })
    },
    updatePassBlock(data) {
      const dataObj = {
        id: this.editablePassBlock.id,
        data
      }
      this.isLoading = true
      this.$store
        .dispatch("passBlocks/updatePassBlock", dataObj)
        .then((response) => {
          const data = response.data.data
          if (data) {
            setTimeout(() => {
              this.$emit("createdPassBlock")
            }, 500)
          }
          this.resetForm()
          this.setSuccessResponse("Successfully updated.")
          this.isLoading = false
          setTimeout(() => {
            this.cancelEdit()
          }, 1000)
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
          this.isLoading = false
        })
    },
    onStartDateChange() {
      const toDate = moment(this.form.to_date).format("MMM DD YYYY")
      const fromDate = moment(this.form.from_date).format("MMM DD YYYY")
      this.form.to_date =
        this.form.to_date && moment(toDate).isSameOrBefore(fromDate, "day")
          ? this.form.from_date
          : this.form.to_date
    },
    resetForm() {
      this.v$.$reset()
      this.form = {
        from_date: moment().format("MMM DD YYYY HH:mm"),
        to_date: moment().format("MMM DD YYYY HH:mm"),
        reason: "",
        message: "",
        from_time: moment().format("HH:mm"),
        to_time: moment().add(1, "hour").format("HH:mm"),
        kiosk: true,
        students: true,
        proxy: true,
        appointments: true,
        recurrence_type: null,
        recurrence_days: []
      }
      this.$nextTick(() => {
        this.v$.$reset()
      })
    },
    resetResponseMessages() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    setSuccessResponse(message) {
      this.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    },
    setErrorResponse(message, errors) {
      this.reqResponse = {
        type: "danger",
        message: message ? message : "Someting went wrong!",
        errors: errors ? errors : {}
      }
    },
    cancelEdit() {
      this.$store.commit("passBlocks/SET_EDITABLE_PASS_BLOCK", null)
      this.resetForm()
      this.resetResponseMessages()
    },
    refreshForm() {
      this.resetForm()
      this.resetResponseMessages()
    },
    goToFullMenu() {
      this.$emit("closeModal")
    },
    toggleDate(toggle) {
      if (this.isEditableFromDate) {
        toggle()
      }
    }
  }
}
</script>
